import { date } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { Button } from 'components/Button/Button';
import { OverlayModal } from 'components/OverlayModal/OverlayModal';
import { OverlayModalBody } from 'components/OverlayModal/OverlayModalBody';
import { OverlayModalFooter } from 'components/OverlayModal/OverlayModalFooter';
import { OverlayModalHeader } from 'components/OverlayModal/OverlayModalHeader';
import { useLocale } from 'containers/App/LocaleContext';
import { pipe } from 'fp-ts/lib/pipeable';
import { useMemo } from 'react';
import type { FC } from 'react';
import type { Delivery } from './WebHooksState';

type Props = {
  delivery: Delivery;
  timezone: date.Timezone;
  dateTimeFormat: string;
  onClose: () => void;
};

export const DeliveryDialog: FC<Props> = ({
  delivery: { request, response, timestamp, url, error },
  dateTimeFormat,
  timezone,
  onClose,
}) => {
  const msg = useLocale();

  const formattedRequestTime = useMemo(
    () =>
      request
        ? pipe(request.body, prop('timestamp'), (ts) =>
            date.formatUtcTimestampInTimeZone(ts, timezone, dateTimeFormat),
          )
        : null,
    [request, dateTimeFormat, timezone],
  );

  return (
    <OverlayModal onClose={onClose} contentClassName='add-path-verification'>
      <OverlayModalHeader>
        <h4>{msg.settings.webHooks.deliveries.deliveryDetail}</h4>
      </OverlayModalHeader>
      <OverlayModalBody>
        <p>
          <strong>{msg.settings.webHooks.deliveries.url}</strong> {url}
        </p>
        <p>
          <strong>{msg.settings.webHooks.deliveries.time}</strong>{' '}
          {date.formatUtcTimestampInTimeZone(
            timestamp,
            timezone,
            dateTimeFormat,
          )}
        </p>
        {request && (
          <>
            <h4>{msg.settings.webHooks.deliveries.request}</h4>
            <strong>{msg.settings.webHooks.deliveries.body}</strong>
            <pre>{JSON.stringify(request.body, null, 4)}</pre>
            <p>
              <strong>{msg.settings.webHooks.deliveries.time}</strong>{' '}
              {formattedRequestTime}
            </p>
            <p>
              <strong>{msg.settings.webHooks.deliveries.hash}</strong>{' '}
              {request.hash}
            </p>
          </>
        )}
        {error && (
          <p>
            <strong>{msg.settings.webHooks.deliveries.errorLabel}</strong>{' '}
            {error}
          </p>
        )}
        {response && (
          <>
            <h4>{msg.settings.webHooks.deliveries.response}</h4>
            <strong>{msg.settings.webHooks.deliveries.headers}</strong>
            <pre>{JSON.stringify(response.headers, null, 4)}</pre>
            <p>
              <strong>{msg.settings.webHooks.deliveries.status}</strong>{' '}
              {response.status} {response.statusText}
            </p>
          </>
        )}
      </OverlayModalBody>
      <OverlayModalFooter>
        <Button variant='secondary' type='button' onClick={onClose}>
          {msg.settings.webHooks.btnClose}
        </Button>
      </OverlayModalFooter>
    </OverlayModal>
  );
};
